import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface CopyableTextProps {
  /** What the user sees; defaults to the copied value. */
  label?: string;
  value: string;
  monospace?: boolean;
  /** Tooltip on the text itself — used to show a full hash behind its truncated form. */
  title?: string;
}

export function CopyableText({ label, value, monospace = true, title }: CopyableTextProps) {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can be refused (insecure origin, permission); nothing useful to recover.
    }
  };

  const text = (
    <Typography
      component="span"
      variant="body2"
      sx={{ fontFamily: monospace ? 'monospace' : undefined, wordBreak: 'break-all' }}
    >
      {label ?? value}
    </Typography>
  );

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%' }}>
      {title ? <Tooltip title={title}>{text}</Tooltip> : text}
      <Tooltip title={copied ? t('actions.copied') : t('actions.copy')}>
        <IconButton size="small" onClick={() => void copy()} aria-label={t('actions.copyToClipboard')}>
          {copied ? <DoneIcon fontSize="inherit" /> : <ContentCopyIcon fontSize="inherit" />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}
